import { GAMIFICATION_ROUTES } from "./gamificationRoutes";
import React, { useEffect, useMemo, useRef } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";
import BattleCard from "./BattleCard";
import GamificationActivityDialog from "./GamificationActivityDialog";
import { useGamificationAttemptActions } from "../../hooks/useGamificationAttemptActions";
import { normalizeAttemptedGamificationData } from "../../hooks/useAttemptedGamificationList";
import { fetchAttemptedGamificationList } from "../../services/gamificationService";
import { buildGamificationFlowState, getStoredGamificationFlowState } from "./gamificationFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import "./StartGamification.css";

const GamificationAttempts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sentinelRef = useRef(null);
  const flowState = useMemo(
    () => buildGamificationFlowState(location.state?.flowState || location.state || {}, getStoredGamificationFlowState()),
    [location.state]
  );
  const stateId = Number(flowState?.state_id || flowState?.selectedState?.state_id || 13);
  const categoryId = Number(flowState?.category_id || flowState?.selectedCategory?.category_id || 6);
  const seed = location.state?.initialData?.attempts ? location.state.initialData : null;
  const queryKey = ["gamificationAttempts", stateId, categoryId];
  const {
    data, isPending, isFetchingNextPage, hasNextPage, fetchNextPage, isFetchNextPageError,
    isError, error,
  } = useInfiniteQuery({
    queryKey,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const { payload } = await fetchAttemptedGamificationList({
        states_id: stateId, category_id: categoryId, page: pageParam,
      });
      return normalizeAttemptedGamificationData(payload, pageParam);
    },
    getNextPageParam: (lastPage) => {
      const current = Number(lastPage?.pagination?.currentPage || 1);
      const total = Number(lastPage?.pagination?.totalPages || 1);
      return current < total ? current + 1 : undefined;
    },
    initialData: seed ? { pages: [seed], pageParams: [1] } : undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: 1,
  });
  const attempts = data?.pages?.flatMap((page) => page.attempts || []) || [];
  const { onAction, notice, dismissNotice, error: actionError, rejoiningRoomId } =
    useGamificationAttemptActions(flowState, GAMIFICATION_ROUTES.attempts);

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError || !sentinelRef.current ||
        typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) fetchNextPage();
    }, { rootMargin: "240px" });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchNextPageError, isFetchingNextPage]);

  return (
    <HomeDashboardLayout showHeader={false}>
      <div className="qbl-page qbankList-page page-padding start-gamification">
        <div className="start-quiz qbl-main instruction-main">
          <div className="hideProfile-page">
            <div className="categories-page-header mb-0">
              <button type="button" className="categories-back-button" aria-label="Go back"
                onClick={() => navigate(GAMIFICATION_ROUTES.start, {
                  state: { ...flowState, gamificationBackTarget: flowState.homeBackTarget || null },
                })}>
                <i className="bi bi-chevron-left" />
              </button>
              <h1 className="categories-page-title">Battle Activity</h1>
              <HeaderProfileSection />
            </div>
          </div>
          {isPending ? <p className="battle-activity__message">Loading battles...</p> : null}
          {isError && !data ? <p className="choose-state__status choose-state__status--error" role="alert">
            {error?.message || "Unable to load battles."}
          </p> : null}
          {actionError ? <p className="choose-state__status choose-state__status--error" role="alert">{actionError}</p> : null}
          {!isPending && (!isError || data) ? (
            <BattleCard attempts={attempts} onAction={onAction} rejoiningRoomId={rejoiningRoomId} />
          ) : null}
          {hasNextPage ? (
            <div ref={sentinelRef} className="battle-activity__load-more">
              {isFetchNextPageError ? <p role="alert">Could not load more battles. Your loaded activity is still available.</p> : null}
              <button type="button" className="battle-pagination__nav" disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}>
                {isFetchingNextPage ? "Loading more..." : isFetchNextPageError ? "Retry" : "Load more"}
              </button>
            </div>
          ) : null}
        </div>
        <MyWalletSidebar withTopOffset={false} />
      </div>
      <GamificationActivityDialog notice={notice} onClose={dismissNotice} />
    </HomeDashboardLayout>
  );
};

export default GamificationAttempts;
